import { LinkTo } from '@ember/routing';
import { Section, VStack } from 'tio-reimagined/components/layout';
import Text from 'tio-reimagined/components/text';
import Chip from 'tio-reimagined/components/chip';
import PageHero from 'tio-reimagined/components/page-hero';
import DebtJourneyHero from 'tio-employee/components/reimagined/debt-journey-hero';
import style from 'ember-style-modifier/modifiers/style';
import { PhArrowLeft, PhPaintBrush, PhCode, PhPalette, PhSparkle } from 'ember-phosphor-icons';

<template>
  {{! template-lint-disable no-bare-strings }}
  <VStack class="w-full min-w-0">
    <div class="mb-4">
      <LinkTo
        @route="authenticated.reimagined.components"
        class="tio-inline-row gap-2 tio-fg-brand dark:text-violet-400 hover:tio-fg-brand dark:hover:text-violet-300 font-medium"
      >
        <PhArrowLeft @size={{18}} />
        Back to Components
      </LinkTo>
    </div>

    <PageHero
      @title="Gradients"
      @subtitle="Figma-driven branded gradient backgrounds for hero banners and feature sections."
      @icon={{PhPalette}}
      @gradient="brand"
      @decoration="circles"
    >
      <:actions>
        <Chip @intent="primary">CSS Utility</Chip>
        <Chip @intent="success">Figma Synced</Chip>
      </:actions>
    </PageHero>

    <div class="space-y-8">

      {{! ========================================
            PageHero gradient presets
            ======================================== }}
      <Section>
        <:header>
          <div class="tio-row-tight">
            <PhPaintBrush @size={{20}} class="tio-fg-brand" />
            <span>PageHero gradient presets</span>
            <Chip @size="sm" @intent="primary">brand-leaning</Chip>
          </div>
        </:header>
        <:body>
          <Text @variant="muted" class="mb-4">
            A small curated set of brand-leaning gradient presets routed through TIO tokens. Dark
            mode follows the canonical palette automatically. Decorative — these gradients are
            visual variety, not status signals.
          </Text>

          <div class="space-y-6">
            <PageHero
              @gradient="brand"
              @title="brand"
              @subtitle="Canonical purple gradient. The default; use for primary brand surfaces."
              @icon={{PhSparkle}}
              @decoration="circles"
            />

            <PageHero
              @gradient="brand-soft"
              @title="brand-soft"
              @subtitle="Pale purple wash for surfaces that wrap dark on-brand content (e.g. the TAS dashboard summary)."
              @icon={{PhPalette}}
            />

            <PageHero
              @gradient="cool"
              @title="cool"
              @subtitle="Teal → purple. Brand-adjacent cool variation — uses the canonical CTA color."
              @icon={{PhSparkle}}
              @decoration="dots"
            />

            <PageHero
              @gradient="warm"
              @title="warm"
              @subtitle="Purple → red. Brand-adjacent warm variation; intentionally not a status red."
              @icon={{PhSparkle}}
            />

            <PageHero
              @gradient="neutral"
              @title="neutral"
              @subtitle="Warm-grey neutrals routed through the TIO black scale."
              @icon={{PhCode}}
            />

            <PageHero
              @gradient="hero"
              @title="hero"
              @subtitle="Figma multi-glow gradient on dark charcoal. The 'premium' pick for landing surfaces."
              @icon={{PhPalette}}
              @decoration="circles"
            />

            {{! DebtJourneyHero production usage }}
            <div>
              <Text @weight="medium" class="mb-2">DebtJourneyHero (Production Component)</Text>
              <DebtJourneyHero
                @currentBalance={{45230}}
                @originalBalance={{68500}}
                @estimatedPayoffDate="2029-08-15"
                @yearProgressAmount={{2340}}
              />
            </div>
          </div>

          {{! Usage patterns }}
          <div class="mt-6 space-y-3">
            <Text @weight="medium">Usage</Text>

            <div class="tio-bg-sunken dark:bg-gray-900 rounded-lg p-4">
              <div class="tio-row-top">
                <PhCode @size={{20}} class="tio-fg-brand shrink-0 mt-0.5" />
                <div>
                  <Text @weight="medium">PageHero with @gradient preset</Text>
                  <pre class="tio-fg-secondary font-mono text-sm mt-1"><code>&lt;PageHero
                      @gradient="brand" @title="..." /&gt;</code></pre>
                </div>
              </div>
            </div>
          </div>
        </:body>
      </Section>

      {{! ========================================
            Design Tokens Reference
            ======================================== }}
      <Section>
        <:header>
          <div class="tio-row-tight">
            <PhPalette @size={{20}} class="tio-fg-brand" />
            <span>Color Tokens</span>
          </div>
        </:header>
        <:body>
          <Text @variant="muted" class="mb-4">
            The gradient uses two key brand colors layered as radial glows. These values come
            directly from the Figma design system.
          </Text>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="rounded-lg overflow-hidden ring-1 ring-gray-200">
              <div class="h-16" {{style background="#231e29"}}></div>
              <div class="p-3 bg-white dark:bg-gray-900">
                <Text @size="sm" @weight="medium" class="font-mono">#231e29</Text>
                <Text @variant="muted" @size="xs">Base — Dark Charcoal</Text>
              </div>
            </div>

            <div class="rounded-lg overflow-hidden ring-1 ring-gray-200">
              <div class="h-16" {{style background="rgb(6, 89, 107)"}}></div>
              <div class="p-3 bg-white dark:bg-gray-900">
                <Text @size="sm" @weight="medium" class="font-mono">rgb(6, 89, 107)</Text>
                <Text @variant="muted" @size="xs">Accent — Teal</Text>
              </div>
            </div>

            <div class="rounded-lg overflow-hidden ring-1 ring-gray-200">
              <div class="h-16" {{style background="rgb(130, 51, 214)"}}></div>
              <div class="p-3 bg-white dark:bg-gray-900">
                <Text @size="sm" @weight="medium" class="font-mono">rgb(130, 51, 214)</Text>
                <Text @variant="muted" @size="xs">Accent — Purple</Text>
              </div>
            </div>
          </div>
        </:body>
      </Section>

    </div>
  </VStack>
</template>
